'use client'

import { useState } from 'react'

import useExposeBuyerProtectionFeeDiscountTest from 'hooks/useExposeBuyerProtectionFeeDiscountTest'
import ContentLoader from 'components/ContentLoader'
import ErrorBoundary from 'components/ErrorBoundary'
import SellerPromotion from 'components/SellerPromotion'
import { ResponseError } from 'types/api'

import useFetchHomepageBlocks from './hooks/useFetchHomepageBlocks'
import BlockArrangement from './common/BlockArrangement'
import ErrorModal from './common/ErrorModal'
import HomeTabs from './HomeTabs'
import HomeBlocks from './HomeBlocks'
import HomeBanners from './HomeBanners'
import Feed from './Feed'
import useTabs from './hooks/useTabs'
import { useHomeContext } from './HomeProvider'
import useHomeObservability from './hooks/useHomeObservability'
import BlocksObserver from './common/BlocksObserver'

type Props = {
  visitsCount: number
}

const Home = ({ visitsCount }: Props) => {
  const [showErrorModal, setShowErrorModal] = useState(false)

  const { currentTab } = useTabs()
  const { homepageSessionId } = useHomeContext()
  const { blocks, areBlocksLoading, fetchNewTab, error, fetchMoreBlocks } = useFetchHomepageBlocks()
  const { trackBoundaryError, trackEmptyFeed } = useHomeObservability(error)

  const hasNextPage = !!blocks?.nextPageToken

  useExposeBuyerProtectionFeeDiscountTest()

  const handleEmptyFeed = (feedError: ResponseError) => {
    trackEmptyFeed(feedError)
    if (error) setShowErrorModal(true)
  }

  const closeModal = () => setShowErrorModal(false)

  const handlePageEnd = () => {
    if (!blocks?.nextPageToken) return

    fetchMoreBlocks({
      tab: currentTab,
      homepageSessionId,
      nextPageToken: blocks.nextPageToken,
    })
  }

  return (
    <ErrorBoundary
      FallbackComponent={() => <ErrorModal onCloseClick={closeModal} />}
      preventLog
      onError={trackBoundaryError}
    >
      <HomeTabs onTabClick={tab => fetchNewTab({ tab, homepageSessionId, nextPageToken: null })} />
      {currentTab.isSellerPromotionEnabled && <SellerPromotion visitsCount={visitsCount} />}
      <div className="container">
        <div className="homepage">
          <HomeBanners />
          {blocks && <HomeBlocks blocks={blocks} />}
          {hasNextPage && !areBlocksLoading && <BlocksObserver onPageEnd={handlePageEnd} />}
          {areBlocksLoading && (
            <ContentLoader
              size={ContentLoader.Size.Large}
              styling={ContentLoader.Styling.Tight}
              testId="homepage-loader"
            />
          )}
          {!hasNextPage && !areBlocksLoading && (
            <BlockArrangement afterBlockSpacer={false}>
              <Feed onEmpty={handleEmptyFeed} />
            </BlockArrangement>
          )}
        </div>
      </div>
      {showErrorModal && <ErrorModal onCloseClick={closeModal} />}
    </ErrorBoundary>
  )
}

export default Home
